import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { confirmAction, showWarning } from '@/lib/swal';
import { CommitNumberInput } from './CommitNumberInput';
import { MAX_TABLE_SEATS, MIN_TABLE_SEATS, reflowTable, resizeTableSeats } from './tableSeating';

export interface TableConfig {
  id: string;
  name: string;
  type: 'circular' | 'rectangular' | 'square' | 'oval';
  seats: number;
  x: number;
  y: number;
  rotation: number;
  width: number;
  height: number;
  // Seat placement options for rectangular and square tables
  seatPlacement?: {
    bothSides: boolean;
    oneSide: boolean;
    leftEnd: boolean;
    rightEnd: boolean;
    noSeats: boolean;
    // Square table specific options
    topSide?: boolean;
    bottomSide?: boolean;
    leftSide?: boolean;
    rightSide?: boolean;
  };
  // Optional explicit per-side seat counts (takes precedence when present)
  seatCounts?: {
    top?: number;
    bottom?: number;
    left?: number; // for square tables
    right?: number; // for square tables
    leftEnd?: number; // 0 or 1 for rectangular
    rightEnd?: number; // 0 or 1 for rectangular
  };
  // Visual properties
  color: string;
  chairColor?: string;
  seatSize?: number;
  // Seat numbering priority (lower number = higher priority)
  seatNumberPriority?: number;
  seating: Array<{
    seatNumber: number;
    rsvpId: string | null;
    role: string | null;
    tag?: string | null;
    x: number;
    y: number;
  }>;
}

interface TableConfigModalProps {
  table: TableConfig | null;
  onClose: () => void;
  onSave: (table: TableConfig) => void;
  /** Live preview: every edit is applied to the plan straight away (and reverted on Cancel). */
  onUpdate?: (table: TableConfig) => void;
  onDelete?: (tableId: string) => void;
  /** Display names for guests, used when warning that a change unseats someone. */
  getGuestName?: (rsvpId: string) => string | null;
}

const TABLE_COLOURS = ['#ffffff', '#8B4513', '#2c3e50', '#e74c3c', '#3498db', '#2ecc71'];
const CHAIR_COLOURS = ['#e5e7eb', '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];

export default function TableConfigModal({
  table,
  onClose,
  onSave,
  onUpdate,
  onDelete,
  getGuestName,
}: TableConfigModalProps) {
  const [config, setConfig] = useState<TableConfig | null>(
    table ? { ...table, seatSize: table.seatSize ?? 28 } : null
  );
  /** The table as it was when the modal opened, restored if the user cancels. */
  const originalRef = useRef<TableConfig | null>(table);
  const changedRef = useRef(false);

  useEffect(() => {
    if (table?.id !== originalRef.current?.id) {
      originalRef.current = table;
      changedRef.current = false;
    }
    setConfig(table ? ({ ...table, seatSize: table.seatSize ?? 28 } as TableConfig) : null);
    // Only re-seed when a different table is opened; live previews come back in as `table` too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table?.id]);

  const handleCancelRef = useRef<() => void>(() => {});
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Leave Escape to any confirmation dialog stacked on top.
      if (e.key === 'Escape' && !document.querySelector('.swal2-container')) handleCancelRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (!config) return null;

  const handleSave = () => {
    changedRef.current = false;
    onSave(config);
    onClose();
  };

  const handleCancel = () => {
    if (changedRef.current && originalRef.current && onUpdate) {
      onUpdate(originalRef.current);
    }
    changedRef.current = false;
    onClose();
  };
  handleCancelRef.current = handleCancel;

  const handleDelete = async () => {
    if (!onDelete) return;
    const seatedCount = config.seating.filter((s) => s.rsvpId).length;
    const result = await confirmAction({
      title: 'Delete Table',
      text: `Are you sure you want to delete "${config.name}"?${
        seatedCount > 0 ? ` ${seatedCount} seated guest${seatedCount > 1 ? 's' : ''} will be unassigned.` : ''
      } You can use Undo to bring it back.`,
      icon: 'warning',
    });
    if (!result.isConfirmed) return;
    onDelete(config.id);
    onClose();
  };

  const apply = (updatedConfig: TableConfig, unseatedGuestIds: string[] = []) => {
    changedRef.current = true;
    setConfig(updatedConfig);
    onUpdate?.(updatedConfig);
    if (unseatedGuestIds.length > 0) {
      const names = unseatedGuestIds.map((id) => getGuestName?.(id) ?? 'A guest').join(', ');
      showWarning(`No longer fits at this table and was unseated: ${names}. Cancel to revert.`);
    }
  };

  /** Plain property edits that don't move seats. */
  const updateConfig = (updates: Partial<TableConfig>) => apply({ ...config, ...updates });

  /** Layout edits: seats are re-laid out and guests kept on their side where possible. */
  const updateLayout = (updates: Partial<TableConfig>) => {
    const result = reflowTable(config, updates);
    apply(result.table, result.unseatedGuestIds);
  };

  const handleSeatsChange = (newSeats: number) => {
    const result = resizeTableSeats(config, newSeats);
    apply(result.table, result.unseatedGuestIds);
  };

  const inputClass = 'w-24';
  const labelClass = 'block text-sm font-medium text-foreground';
  const sectionClass = 'rounded-lg bg-muted/50 p-6 space-y-6';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="table-config-title"
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl border border-border bg-card text-card-foreground shadow-2xl"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-xl border-b border-border bg-card px-6 py-5">
          <div>
            <h2 id="table-config-title" className="text-2xl font-bold text-primary">Configure Table</h2>
            <p className="mt-1 text-sm text-muted-foreground">Customize your table settings and layout</p>
          </div>
          <Button variant="ghost" size="icon" onClick={handleCancel} aria-label="Close">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-6 space-y-8">
          {/* Basic Information */}
          <div className={sectionClass}>
            <h3 className="text-lg font-semibold text-foreground">Basic Information</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className={labelClass}>Table Name</label>
                <Input
                  value={config.name}
                  onChange={(e) => updateConfig({ name: e.target.value })}
                  placeholder="e.g., Head Table, Table 1"
                />
              </div>

              <div className="space-y-2">
                <label className={labelClass}>Table Type</label>
                <Select
                  value={config.type}
                  onValueChange={(value) =>
                    // Per-side counts belong to the old shape.
                    updateLayout({ type: value as TableConfig['type'], seatCounts: undefined })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select table type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rectangular">🔲 Rectangular</SelectItem>
                    <SelectItem value="square">⬜ Square</SelectItem>
                    <SelectItem value="circular">⭕ Circular</SelectItem>
                    <SelectItem value="oval">🥚 Oval</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <label className={labelClass}>Number of Seats</label>
              <div className="flex items-center gap-3">
                <CommitNumberInput
                  min={MIN_TABLE_SEATS}
                  max={MAX_TABLE_SEATS}
                  value={config.seats}
                  onCommit={handleSeatsChange}
                  className={inputClass}
                />
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label="Remove a seat"
                    onClick={() => handleSeatsChange(config.seats - 1)}
                    className="h-8 w-8 p-0"
                  >
                    -
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label="Add a seat"
                    onClick={() => handleSeatsChange(config.seats + 1)}
                    className="h-8 w-8 p-0"
                  >
                    +
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Dimensions */}
          <div className={sectionClass}>
            <h3 className="text-lg font-semibold text-foreground">Dimensions & Rotation</h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className={labelClass}>Width (metres)</label>
                <CommitNumberInput
                  step="0.1"
                  min={0.5}
                  max={5}
                  decimals={1}
                  value={config.width / 100}
                  onCommit={(metres) => updateLayout({ width: Math.round(metres * 100) })}
                />
              </div>

              <div className="space-y-2">
                <label className={labelClass}>Height (metres)</label>
                <CommitNumberInput
                  step="0.1"
                  min={0.5}
                  max={5}
                  decimals={1}
                  value={config.height / 100}
                  onCommit={(metres) => updateLayout({ height: Math.round(metres * 100) })}
                />
              </div>

              <div className="space-y-2">
                <label className={labelClass}>Rotation (degrees)</label>
                <CommitNumberInput
                  min={0}
                  max={359}
                  value={config.rotation}
                  onCommit={(rotation) => updateConfig({ rotation })}
                />
              </div>
            </div>

            {/* Quick rotation buttons */}
            <div className="space-y-2">
              <label className={labelClass}>Quick Rotate</label>
              <div className="flex gap-2">
                {[0, 90, 180, 270].map((angle) => (
                  <Button
                    key={angle}
                    variant="outline"
                    size="sm"
                    onClick={() => updateConfig({ rotation: angle })}
                    className="text-xs"
                  >
                    {angle}°
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Seat placement removed in favor of on-canvas +/- controls */}

          {/* Visual Properties & Position */}
          <div className={sectionClass}>
            <h3 className="text-lg font-semibold text-foreground">Visual Properties</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Table Color Section */}
              <div className="space-y-3">
                <label className={labelClass}>Table Color</label>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Input
                      type="color"
                      value={config.color}
                      onChange={(e) => updateConfig({ color: e.target.value })}
                      className="h-10 w-12 cursor-pointer p-1"
                    />
                    <Input
                      type="text"
                      value={config.color}
                      onChange={(e) => updateConfig({ color: e.target.value })}
                      placeholder="#8B4513"
                      className="flex-1"
                    />
                  </div>
                  <div className="flex gap-2">
                    {TABLE_COLOURS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => updateConfig({ color })}
                        className="h-6 w-6 rounded border-2 border-border transition-colors hover:border-muted-foreground"
                        style={{ backgroundColor: color }}
                        title={color}
                        aria-label={`Table colour ${color}`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Chair Color Section */}
              <div className="space-y-3">
                <label className={labelClass}>Chair Color</label>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Input
                      type="color"
                      value={config.chairColor || '#e5e7eb'}
                      onChange={(e) => updateConfig({ chairColor: e.target.value })}
                      className="h-10 w-12 cursor-pointer p-1"
                    />
                    <Input
                      type="text"
                      value={config.chairColor || '#e5e7eb'}
                      onChange={(e) => updateConfig({ chairColor: e.target.value })}
                      placeholder="#e5e7eb"
                      className="flex-1"
                    />
                  </div>
                  <div className="flex gap-2">
                    {CHAIR_COLOURS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => updateConfig({ chairColor: color })}
                        className="h-6 w-6 rounded border-2 border-border transition-colors hover:border-muted-foreground"
                        style={{ backgroundColor: color }}
                        title={color}
                        aria-label={`Chair colour ${color}`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Seat Size Section */}
              <div className="space-y-3">
                <label className={labelClass}>Seat Size (px)</label>
                <div className="flex items-center gap-3">
                  <CommitNumberInput
                    min={16}
                    max={72}
                    value={config.seatSize ?? 28}
                    onCommit={(seatSize) => updateConfig({ seatSize })}
                    className={inputClass}
                  />
                  <span className="text-sm text-muted-foreground">Increase for longer names on chairs</span>
                </div>
              </div>
            </div>

            {/* Seat Number Priority Section */}
            <div className="space-y-3">
              <label className={labelClass}>Seat Number Priority</label>
              <div className="flex items-center gap-3">
                <CommitNumberInput
                  min={1}
                  max={999}
                  value={config.seatNumberPriority || 1}
                  onCommit={(seatNumberPriority) => updateConfig({ seatNumberPriority })}
                  className="w-20"
                />
                <span className="text-sm text-muted-foreground">
                  Lower numbers get seat numbers first (1 = highest priority)
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 flex items-center justify-between rounded-b-xl border-t border-border bg-card px-6 py-5">
          <div>
            {onDelete && (
              <Button variant="destructive" onClick={() => void handleDelete()}>
                Delete Table
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleCancel} className="px-6">
              Cancel
            </Button>
            <Button onClick={handleSave} className="px-6">
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
